/**
 * ContributorReviewUI — Review translations one at a time.
 *
 * Read-first, edit-on-demand. Vote/skip is the primary flow.
 * Adaptive context: findings, concepts, AI suggestions shown only when present.
 *
 * Route: /polyglot/review?language=xx&platform=pp&filter=ff
 * API: GET /@polyglot/contributor-review/string
 *      POST /@polyglot/contributor-review/action
 *
 * CR-2026-063 / SPEC-TR-001
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Loader,
  Icon,
  Button,
  Label,
  Message,
  Breadcrumb,
  Table,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

// --- Constants ---

const FILTER_OPTIONS = [
  { key: 'all', value: 'all', text: 'All Strings' },
  { key: 'missing', value: 'missing', text: 'Missing Translations' },
  { key: 'has_findings', value: 'has_findings', text: 'Has QC Findings' },
  { key: 'has_concept', value: 'has_concept', text: 'Has ISO 704 Concept' },
];

const PLATFORM_OPTIONS = [
  { key: '', value: '', text: 'All Platforms' },
  { key: 'nick', value: 'nick', text: 'Nick' },
  { key: 'volto', value: 'volto', text: 'Volto' },
  { key: 'volto_slate', value: 'volto_slate', text: 'Volto Slate' },
  { key: 'volto_vlt', value: 'volto_vlt', text: 'Volto VLT' },
  { key: 'plone', value: 'plone', text: 'Plone' },
];

const NOTE_TYPE_OPTIONS = [
  { key: 'comment', value: 'comment', text: 'Comment' },
  { key: 'feedback', value: 'feedback', text: 'Feedback' },
  { key: 'translator_note', value: 'translator_note', text: 'Translator note' },
  { key: 'question', value: 'question', text: 'Question' },
];

const sourceColor = (src) => ({
  ai_generated: 'orange', imported: 'blue', corrected: 'green',
  copied: 'grey', po_import: 'teal', gap_fill: 'purple',
}[src] || 'grey');

// --- Internal Components ---

/** Collapsed reference context — same pattern as QCReviewQueue */
function ContextPanel({ context, language }) {
  const [open, setOpen] = useState(false);
  if (!context) return null;

  const { conventions, standard_translations, preserved_terms, siblings, terminology } = context;
  const hasContext = (standard_translations?.length > 0) || (preserved_terms?.length > 0) ||
    (siblings?.length > 0) || (terminology?.length > 0) || conventions;
  if (!hasContext) return null;

  return (
    <div style={{ marginTop: '0.8em', marginBottom: '0.8em' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', fontSize: '0.85em', color: '#2185d0', display: 'flex', alignItems: 'center', gap: '0.3em' }}
      >
        <Icon name={open ? 'chevron down' : 'chevron right'} size="small" />
        Reference Context
        {siblings?.length > 0 && <Label size="mini" circular>{siblings.length} siblings</Label>}
        {terminology?.length > 0 && <Label size="mini" circular color="teal">{terminology.length} terms</Label>}
        {standard_translations?.length > 0 && <Label size="mini" circular color="green">{standard_translations.length} standard</Label>}
      </div>
      {open && (
        <div style={{ marginTop: '0.5em', fontSize: '0.9em' }}>
          {conventions && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Conventions:</strong>{' '}
              {conventions.register && <span>Register: {conventions.register}. </span>}
              {conventions.button_style && <span>Buttons: {conventions.button_style}. </span>}
              {conventions.notes && <div style={{ color: '#666', marginTop: '0.2em', whiteSpace: 'pre-line' }}>{conventions.notes}</div>}
            </div>
          )}
          {preserved_terms?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <Label color="purple" size="mini"><Icon name="lock" /> Preserved term — keep in English</Label>
            </div>
          )}
          {standard_translations?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Standard translation:</strong>
              {standard_translations.map((st, i) => (
                <div key={i} style={{ marginLeft: '1em' }}>
                  &ldquo;{st.translation}&rdquo; {st.context && `(${st.context})`}
                  {st.source && <span style={{ color: '#888' }}> — {st.source}</span>}
                </div>
              ))}
            </div>
          )}
          {terminology?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Terminology:</strong>
              {terminology.map((t, i) => (
                <div key={i} style={{ marginLeft: '1em' }}>
                  &ldquo;{t.translation}&rdquo; ({t.source}{t.reliability && `, R${t.reliability}`})
                </div>
              ))}
            </div>
          )}
          {siblings?.length > 0 && (
            <Table compact size="small" basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Language</Table.HeaderCell>
                  <Table.HeaderCell>Translation</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {siblings.map((s, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>
                      <strong>{s.language}</strong>
                      {s.language_name_english && <span style={{ color: '#888' }}> ({s.language_name_english})</span>}
                    </Table.Cell>
                    <Table.Cell>{s.msgstr}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

/** Validation feedback after correction submission */
function ValidationFeedback({ validation }) {
  if (!validation) return null;
  const isPass = validation.result === 'pass';
  const isWarn = validation.result === 'warn';
  const color = isPass ? 'green' : isWarn ? 'yellow' : 'red';
  const icon = isPass ? 'check circle' : isWarn ? 'warning sign' : 'times circle';
  const title = isPass ? 'Correction applied'
    : isWarn ? 'Applied with warnings'
    : 'Validation failed — please revise';

  return (
    <Message color={color} icon size="small" style={{ marginTop: '0.8em' }}>
      <Icon name={icon} />
      <Message.Content>
        <Message.Header>{title}</Message.Header>
        {validation.checks?.filter((c) => c.result !== 'pass').map((c, i) => (
          <p key={i} style={{ margin: '0.2em 0' }}>
            <strong>{c.name}</strong>: {c.reason}
          </p>
        ))}
      </Message.Content>
    </Message>
  );
}

// === MAIN COMPONENT ===

export default function ContributorReviewUI() {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);

  const [trainingChecked, setTrainingChecked] = useState(false);
  const [needsTraining, setNeedsTraining] = useState(false);

  // Check training status before allowing review
  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/contributors/me`)
      .then((profile) => {
        if (!profile.training_completed_at) {
          setNeedsTraining(true);
        }
        setTrainingChecked(true);
      })
      .catch(() => {
        setTrainingChecked(true);
      });
  }, []);

  const language = params.get('language') || '';
  const [filter, setFilter] = useState(params.get('filter') || 'all');
  const [platform, setPlatform] = useState(params.get('platform') || '');

  // Data
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // String list for scrollable table
  const [stringList, setStringList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const activeRowRef = useRef(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [correction, setCorrection] = useState('');
  const [rationale, setRationale] = useState('');
  const [validation, setValidation] = useState(null);

  // Note
  const [noteType, setNoteType] = useState('comment');
  const [noteText, setNoteText] = useState('');

  // Action state
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [voteRecorded, setVoteRecorded] = useState(null); // 'looks_good' | 'needs_attention' | null

  // Session stats
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, corrected: 0 });

  // --- Fetch ---

  const fetchString = useCallback(
    async (opts = {}) => {
      if (!language) return;
      setLoading(true);
      setError(null);
      setEditing(false);
      setValidation(null);
      setActionMsg(null);
      setNoteText('');
      setVoteRecorded(null);

      const qp = new URLSearchParams();
      qp.set('language', language);
      qp.set('filter', opts.filter || filter);
      const plat = opts.platform !== undefined ? opts.platform : platform;
      if (plat) qp.set('platform', plat);
      if (opts.message_id) qp.set('message_id', opts.message_id);
      else if (opts.offset !== undefined) qp.set('offset', opts.offset);

      try {
        const result = await fetchApi(
          `${getApiPath()}/@polyglot/contributor-review/string?${qp.toString()}`,
        );
        setData(result);
        // Pre-check existing vote from server
        setVoteRecorded(result?.votes?.my_vote || null);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    },
    [language, filter, platform],
  );

  useEffect(() => {
    fetchString();
  }, [fetchString]);

  // Fetch lightweight list for scrollable table
  const fetchList = useCallback(async () => {
    if (!language) return;
    setListLoading(true);
    const qp = new URLSearchParams();
    qp.set('language', language);
    qp.set('list', 'true');
    qp.set('filter', filter);
    if (platform) qp.set('platform', platform);
    try {
      const result = await fetchApi(
        `${getApiPath()}/@polyglot/contributor-review/string?${qp.toString()}`,
      );
      setStringList(result.items || []);
    } catch (err) {
      // List is non-critical — don't block on error
    }
    setListLoading(false);
  }, [language, filter, platform]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Scroll active row into view
  const currentMessageId = data?.string?.message_id;
  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentMessageId]);

  // --- Navigation ---

  const goNext = () => {
    if (data?.navigation?.next_message_id) {
      fetchString({ message_id: data.navigation.next_message_id });
    }
  };
  const goPrev = () => {
    if (data?.navigation?.prev_message_id) {
      fetchString({ message_id: data.navigation.prev_message_id });
    }
  };

  const changeFilter = (_, { value }) => {
    setFilter(value);
    const pp = platform ? `&platform=${platform}` : '';
    history.replace(`/polyglot/review?language=${language}&filter=${value}${pp}`);
    fetchString({ filter: value, offset: 0 });
  };

  const changePlatform = (_, { value }) => {
    setPlatform(value);
    const fp = filter !== 'all' ? `&filter=${filter}` : '';
    const pp = value ? `&platform=${value}` : '';
    history.replace(`/polyglot/review?language=${language}${fp}${pp}`);
    fetchString({ platform: value, offset: 0 });
  };

  // --- Actions ---

  // Vote is a local toggle — submitted with Submit button
  const handleVote = (voteType) => {
    setVoteRecorded((prev) => prev === voteType ? null : voteType);
  };

  const handleSkip = () => {
    setSessionStats((s) => ({ ...s, reviewed: s.reviewed + 1 }));
    goNext();
  };

  const handleStartEdit = () => {
    setEditing(true);
    setCorrection(data?.string?.current_msgstr || '');
    setRationale('');
    setValidation(null);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setCorrection('');
    setRationale('');
    setValidation(null);
  };

  const handleSubmitCorrection = async () => {
    if (!correction.trim() || acting) return;
    setActing(true);
    setValidation(null);
    try {
      const result = await fetchApi(
        `${getApiPath()}/@polyglot/contributor-review/action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message_id: data.string.message_id,
            language,
            action: 'correct',
            suggested_value: correction.trim(),
            rationale: rationale.trim() || null,
          }),
        },
      );
      setValidation(result.validation);
      if (result.applied) {
        setSessionStats((s) => ({ ...s, corrected: s.corrected + 1, reviewed: s.reviewed + 1 }));
        setTimeout(() => goNext(), 1500);
      }
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    }
    setActing(false);
  };

  const handleSubmit = async () => {
    if (acting) return;
    setActing(true);
    try {
      // 1. Send vote attestation if checked (DR-2026-065)
      if (voteRecorded) {
        await fetchApi(
          `${getApiPath()}/@polyglot/contributor-review/action`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message_id: data.string.message_id,
              language,
              action: 'vote',
              verdict: voteRecorded,
            }),
          },
        );
      }

      // 2. Confirm the string as reviewed
      await fetchApi(
        `${getApiPath()}/@polyglot/contributor-review/action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message_id: data.string.message_id,
            language,
            action: 'confirm',
          }),
        },
      );

      setSessionStats((s) => ({ ...s, reviewed: s.reviewed + 1 }));
      const voteLabel = voteRecorded === 'looks_good' ? ' (looks good)' : voteRecorded === 'needs_attention' ? ' (needs work)' : '';
      setActionMsg({ positive: true, text: `Reviewed${voteLabel}` });
      setTimeout(() => goNext(), 800);
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    }
    setActing(false);
  };

  const handleSubmitNote = async () => {
    if (!noteText.trim() || acting) return;
    setActing(true);
    try {
      await fetchApi(
        `${getApiPath()}/@polyglot/contributor-review/action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message_id: data.string.message_id,
            language,
            action: 'note',
            note_type: noteType,
            note_text: noteText.trim(),
          }),
        },
      );
      setActionMsg({ positive: true, text: 'Note added' });
      setNoteText('');
      // Refresh to show the new note in audit trail
      fetchString({ message_id: data.string.message_id });
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    }
    setActing(false);
  };

  // --- No language guard ---

  if (!trainingChecked) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '100px' }} />
      </Container>
    );
  }

  if (needsTraining) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Training Required" />
        <Message warning icon>
          <Icon name="graduation cap" />
          <Message.Content>
            <Message.Header>Complete Training First</Message.Header>
            <p>
              Before you can start reviewing translations, please complete the
              onboarding training. It takes about 10 minutes and covers the
              workflow, quality standards, and tools.
            </p>
            <Link to="/polyglot/training" className="ui primary button" style={{ marginTop: '0.5em' }}>
              <Icon name="arrow right" /> Go to Training
            </Link>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  if (!language) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message warning>
          <Message.Header>No language selected</Message.Header>
          <p>Go to your <Link to="/polyglot/dashboard">Dashboard</Link> and select a language to review.</p>
        </Message>
      </Container>
    );
  }

  const nav = data?.navigation || {};
  const str = data?.string;
  const concept = data?.concept;
  const audit = data?.audit_trail;
  const langName = data?.context?.conventions?.language_name_english || language;

  // Adaptive: check what context is available
  const findings = audit?.qc_findings?.filter((f) => f.status !== 'fixed_local' && f.status !== 'acceptable') || [];
  const hasFinding = findings.length > 0;
  const hasConcept = !!concept;
  const hasSuggestedFix = findings.some((f) => f.suggested_fix);

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '1100px' }}>
      <Helmet title={`Review ${langName}`} />

      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to={`/polyglot/browse/${platform || 'nick'}${language ? `?language=${language}` : ''}`}>Browse</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Review {langName}</Breadcrumb.Section>
      </Breadcrumb>

      {/* Navigation bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.8em',
        marginBottom: '1em', justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
          <Button basic compact disabled={!nav.prev_message_id || loading} onClick={goPrev} title="Previous">
            <Icon name="chevron left" /> Prev
          </Button>
          <span style={{ fontSize: '0.95em', color: '#666', fontWeight: 'bold', minWidth: '80px', textAlign: 'center' }}>
            {nav.position || 0} of {nav.total || 0}
          </span>
          <Button basic compact disabled={!nav.next_message_id || loading} onClick={goNext} title="Next">
            Next <Icon name="chevron right" />
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
          <Dropdown
            selection compact size="small"
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={changePlatform}
            placeholder="All Platforms"
            style={{ minWidth: '150px' }}
          />
          <Dropdown
            selection compact size="small"
            options={FILTER_OPTIONS}
            value={filter}
            onChange={changeFilter}
            style={{ minWidth: '170px' }}
          />
        </div>

        <div style={{ fontSize: '0.85em', color: '#888', display: 'flex', gap: '1em' }}>
          <span><Icon name="check" />{sessionStats.reviewed} reviewed</span>
          <span><Icon name="pencil" />{sessionStats.corrected} corrected</span>
        </div>
      </div>

      {/* Error */}
      {error && <Message negative onDismiss={() => setError(null)} size="small">{error}</Message>}

      {/* Action feedback */}
      {actionMsg && (
        <Message
          positive={actionMsg.positive}
          negative={!actionMsg.positive}
          size="small"
          onDismiss={() => setActionMsg(null)}
        >
          {actionMsg.text}
        </Message>
      )}

      {/* Loading */}
      {loading && !str && <Loader active inline="centered" content="Loading..." />}

      {/* Empty state */}
      {!loading && !str && !error && (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="check circle" color="green" />
            No strings match the current filter for {langName}
          </Header>
          <Button as={Link} to="/polyglot/dashboard" primary>
            Back to Dashboard
          </Button>
        </Segment>
      )}

      {/* === The Card === */}
      {str && (
        <Segment raised style={{ opacity: loading ? 0.5 : 1 }}>

          {/* Metadata bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
            <div>
              <Label color="blue" size="small">{str.platform}</Label>
              <Label size="small" color={sourceColor(str.translation_source)}>
                {str.translation_source || 'unknown'}
              </Label>
            </div>
            <Link
              to={`/polyglot/messages/${str.message_id}?language=${language}`}
              style={{ fontSize: '0.9em' }}
            >
              Message #{str.message_id} <Icon name="external" size="small" />
            </Link>
          </div>

          {/* English source — not bold, readable */}
          <div style={{ marginBottom: '0.8em' }}>
            <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
              English
            </div>
            <div style={{ fontSize: '1.2em', lineHeight: '1.4', color: '#333' }}>
              {str.english_source}
            </div>
          </div>

          {/* ISO 704 Concept — directly under English, before translation */}
          {hasConcept && (
            <div style={{ marginBottom: '1em' }}>
              <div style={{
                fontSize: '0.9em', lineHeight: '1.5',
                padding: '0.5em 0.7em',
                background: '#e8f4fd',
                borderRadius: '4px',
                borderLeft: '3px solid #2185d0',
              }}>
                <span style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#2185d0', letterSpacing: '0.05em' }}>
                  <Icon name="lightbulb" size="small" />
                  ISO 704
                </span>
                {concept.subject_field && (
                  <Label size="mini" color="blue" style={{ marginLeft: '0.4em' }}>{concept.subject_field}</Label>
                )}
                <div style={{ marginTop: '0.2em' }}>
                  {concept.definition}
                </div>
                {concept.disambiguations?.length > 0 && (
                  <div style={{ marginTop: '0.3em' }}>
                    {concept.disambiguations.map((d, i) => (
                      <div key={i} style={{ fontSize: '0.85em', color: '#555' }}>
                        <Icon name="exchange" size="small" />
                        vs <strong>{d.contrast_term}</strong>: {d.distinction}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Current translation — bold, with edit pencil beside it */}
          <div style={{ marginBottom: '1em' }}>
            <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
              Current ({language})
            </div>

            {!editing ? (
              /* Read mode — clickable text with visible pencil */
              <div
                onClick={handleStartEdit}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5em',
                  fontSize: '1.2em', fontWeight: '600', lineHeight: '1.4',
                  padding: '0.5em 0.7em',
                  background: '#fff8f0',
                  border: '1px solid #ffe0b2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2185d0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ffe0b2'; }}
              >
                <span style={{ flex: 1 }}>
                  {str.current_msgstr || <span style={{ color: '#ccc', fontStyle: 'italic', fontWeight: '400' }}>— empty —</span>}
                </span>
                <Icon name="pencil" style={{ color: '#aaa', fontSize: '0.7em', flexShrink: 0 }} />
              </div>
            ) : (
              /* Edit mode — textarea with submit/cancel */
              <div>
                <textarea
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  rows={3}
                  autoFocus
                  style={{
                    width: '100%', fontSize: '1.1em', fontWeight: '600',
                    padding: '0.5em 0.7em',
                    border: '2px solid #2185d0',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                <div style={{ marginTop: '0.4em' }}>
                  <input
                    type="text"
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    placeholder="Why this change? (optional)"
                    style={{
                      width: '100%', padding: '0.4em 0.7em',
                      border: '1px solid #ddd', borderRadius: '4px',
                      fontSize: '0.9em', fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.6em', marginTop: '0.5em' }}>
                  <Button
                    color="blue"
                    loading={acting}
                    disabled={acting || !correction.trim()}
                    onClick={handleSubmitCorrection}
                  >
                    <Icon name="paper plane" /> Submit Correction
                  </Button>
                  <Button basic onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Validation feedback */}
          <ValidationFeedback validation={validation} />

          {/* --- Adaptive context: findings + AI suggestion under translation --- */}

          {/* QC Findings (orange accent) */}
          {hasFinding && (
            <div style={{ marginBottom: '1em' }}>
              <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
                Finding
              </div>
              {findings.map((f, i) => (
                <div key={i} style={{
                  fontSize: '1em', lineHeight: '1.5',
                  padding: '0.5em 0.7em',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  borderLeft: '3px solid #f2711c',
                  marginBottom: i < findings.length - 1 ? '0.5em' : 0,
                }}>
                  {f.finding}
                  {f.severity && (
                    <Label size="mini" style={{ marginLeft: '0.5em' }}
                      color={f.severity === 'S4' ? 'red' : f.severity === 'S3' ? 'orange' : 'grey'}>
                      {f.severity}
                    </Label>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* AI Suggestion — clickable to use as correction */}
          {hasSuggestedFix && (
            <div style={{ marginBottom: '1em' }}>
              <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
                AI Suggestion <span style={{ textTransform: 'none', fontStyle: 'italic' }}>(click to use)</span>
              </div>
              {findings.filter((f) => f.suggested_fix).map((f, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setEditing(true);
                    setCorrection(f.suggested_fix);
                    setRationale('Applied AI suggestion');
                    setValidation(null);
                  }}
                  style={{
                    fontSize: '1em', color: '#555',
                    padding: '0.5em 0.7em',
                    background: '#f8f8f8',
                    borderRadius: '4px',
                    border: '1px dashed #ccc',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2185d0'; e.currentTarget.style.background = '#f0f7ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ccc'; e.currentTarget.style.background = '#f8f8f8'; }}
                >
                  {f.suggested_fix}
                  <Icon name="pencil" size="small" style={{ marginLeft: '0.5em', color: '#ccc' }} />
                </div>
              ))}
            </div>
          )}

          {/* Reference Context (collapsed) */}
          <ContextPanel context={data.context} language={language} />

          {/* --- Review actions --- */}
          <div style={{ borderTop: '2px solid #e8e8e8', paddingTop: '1em', marginTop: '0.5em' }}>

            {/* Vote row — checkbox-style toggles with counts */}
            {(() => {
              const votes = data?.votes || {};
              return (
                <div style={{ display: 'flex', gap: '1.2em', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.8em' }}>
                  <label
                    onClick={() => handleVote('looks_good')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.4em',
                      cursor: 'pointer', fontSize: '0.95em',
                      color: voteRecorded === 'looks_good' ? '#21ba45' : '#666',
                      userSelect: 'none',
                    }}
                  >
                    <Icon
                      name={voteRecorded === 'looks_good' ? 'check square' : 'square outline'}
                      color={voteRecorded === 'looks_good' ? 'green' : 'grey'}
                    />
                    Looks good
                    {votes.looks_good > 0 && (
                      <Label size="mini" circular color="green">{votes.looks_good}</Label>
                    )}
                  </label>
                  <label
                    onClick={() => handleVote('needs_attention')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.4em',
                      cursor: 'pointer', fontSize: '0.95em',
                      color: voteRecorded === 'needs_attention' ? '#f2711c' : '#666',
                      userSelect: 'none',
                    }}
                  >
                    <Icon
                      name={voteRecorded === 'needs_attention' ? 'check square' : 'square outline'}
                      color={voteRecorded === 'needs_attention' ? 'orange' : 'grey'}
                    />
                    Needs work
                    {votes.needs_attention > 0 && (
                      <Label size="mini" circular color="orange">{votes.needs_attention}</Label>
                    )}
                  </label>

                  {/* Voter names */}
                  {votes.voters?.length > 0 && (
                    <span style={{ fontSize: '0.8em', color: '#999' }}>
                      {votes.voters.map((v) => v.name).join(', ')}
                      {votes.stale_count > 0 && (
                        <span style={{ color: '#f2711c', marginLeft: '0.5em' }}>
                          ({votes.stale_count} stale)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Submit / Skip / Cancel row */}
            <div style={{ display: 'flex', gap: '0.6em', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button color="blue" compact onClick={handleSubmit} loading={acting} disabled={acting}>
                <Icon name="check" /> Submit
              </Button>
              <Button basic compact onClick={handleSkip} disabled={loading}>
                <Icon name="forward" /> Skip
              </Button>
            </div>
          </div>

          {/* --- Notes/Comments — always-visible text box --- */}
          <div style={{ marginTop: '1em', borderTop: '1px solid #f0f0f0', paddingTop: '0.8em' }}>
            {/* Existing notes */}
            {audit?.contributor_notes?.length > 0 && (
              <div style={{ marginBottom: '0.6em', fontSize: '0.85em' }}>
                {audit.contributor_notes.map((n, i) => (
                  <div key={i} style={{ padding: '0.3em 0', borderBottom: '1px solid #f5f5f5' }}>
                    <Label size="mini" color="blue">{n.note_type}</Label>
                    {' '}<span style={{ color: '#888' }}>{n.contributor_name}</span>
                    {n.note_text && <span> — {n.note_text}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Always-visible note input */}
            <div style={{ display: 'flex', gap: '0.5em', alignItems: 'flex-start' }}>
              <Dropdown
                selection compact size="mini"
                options={NOTE_TYPE_OPTIONS}
                value={noteType}
                onChange={(_, { value }) => setNoteType(value)}
                style={{ minWidth: '130px', flexShrink: 0 }}
              />
              <input
                type="text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && noteText.trim()) handleSubmitNote(); }}
                placeholder="Add a comment, question, or note..."
                style={{
                  flex: 1, padding: '0.5em 0.7em',
                  border: '1px solid #ddd', borderRadius: '4px',
                  fontSize: '0.9em', fontFamily: 'inherit',
                }}
              />
              <Button
                size="small" color="blue" compact
                onClick={handleSubmitNote}
                loading={acting}
                disabled={!noteText.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </Segment>
      )}

      {/* --- Scrollable string list --- */}
      {stringList.length > 0 && (
        <Segment style={{ marginTop: '1.5em' }}>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            <Icon name="list" /> All Strings ({stringList.length})
          </Header>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Table compact size="small" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '50px' }}>Msg</Table.HeaderCell>
                  <Table.HeaderCell>English</Table.HeaderCell>
                  <Table.HeaderCell>Translation</Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '40px' }}></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {stringList.map((s) => {
                  const isActive = s.message_id === currentMessageId;
                  return (
                    <Table.Row
                      key={s.message_id}
                      active={isActive}
                      style={{ cursor: 'pointer' }}
                      onClick={() => fetchString({ message_id: s.message_id })}
                      {...(isActive ? { ref: activeRowRef } : {})}
                    >
                      <Table.Cell><strong>{s.message_id}</strong></Table.Cell>
                      <Table.Cell style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.english_source}
                      </Table.Cell>
                      <Table.Cell style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.msgstr}
                      </Table.Cell>
                      <Table.Cell>
                        {s.has_finding && <Icon name="warning sign" color="orange" size="small" title="Has QC finding" />}
                        {s.has_concept && <Icon name="lightbulb" color="blue" size="small" title="Has ISO 704 concept" />}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        </Segment>
      )}
    </Container>
  );
}
